import { useEffect, useMemo, useState } from 'react'

import { FileUsageState, groupUsage } from 'components/FileUsage'
import { LayoutScroll } from 'components/LayoutPage'
import CheckboxItem from 'components/uiKit/Checkbox/CheckboxItem'
import Loader from 'components/uiKit/Loader'
import { Typography } from 'components/uiKit/Typography'
import { useFileMetaGetById } from 'gql/files/apollo'
import { t } from 'services/Translation'

import FileReplacePreview from '../FileReplacePreview'
import FileUsageProjectItem, { getProjectState } from '../FileUsageProjectItem'
import * as s from './FileUsagesCheckboxList.module.scss'

interface IFileUsagesCheckboxListProps {
  sourceFileMetaId?: string
  companyId: string
  onChange: (checkedBlocksUsageIds: string[]) => void
}

export type CheckedBlocksMap = Record<string, boolean>

const getAllState = (fileUsages: FileUsageState[], checkedBlocks: CheckedBlocksMap) => {
  const result = fileUsages.map((usage) => getProjectState(usage, checkedBlocks))
  return {
    checked: result.every((r) => r[0].checked),
    indeterminate:
      result.some((r) => r[0].indeterminate || r[0].checked) && !result.every((r) => r[0].checked),
  }
}

const FileUsagesCheckboxList = ({
  sourceFileMetaId,
  companyId,
  onChange,
}: IFileUsagesCheckboxListProps) => {
  const { data, loading } = useFileMetaGetById(sourceFileMetaId || '')
  const [checkedBlocks, setCheckedBlocks] = useState<CheckedBlocksMap>({})
  const fileUsages = useMemo(
    () => groupUsage(data?.data?.fileUsage || [], companyId),
    [data?.data?.fileUsage, companyId],
  )

  const allState = useMemo(
    () => getAllState(fileUsages, checkedBlocks),
    [fileUsages, checkedBlocks],
  )

  const handleToggleAll = () => {
    const value = !allState.checked
    setCheckedBlocks((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((key) => {
        next[key] = value
      })
      return next
    })
  }

  const handleToggleProject = (projectId: string, checked: boolean) => {
    const project = fileUsages.find((p) => p.projectId === projectId)
    if (!project) {
      return
    }

    setCheckedBlocks((prev) => {
      const next = { ...prev }
      project.sections.forEach((section) => {
        section.blocks.forEach((block) => {
          next[block.usageId] = checked
        })
      })
      return next
    })
  }

  const handleToggleSection = (projectId: string, sectionId: string, checked: boolean) => {
    const section = fileUsages
      .find((p) => p.projectId === projectId)
      ?.sections.find((s) => s.id === sectionId)

    if (!section) {
      return
    }

    setCheckedBlocks((prev) => {
      const next = { ...prev }
      section.blocks.forEach((block) => {
        next[block.usageId] = checked
      })
      return next
    })
  }

  const handleToggleBlock = (fileUsageId: string) => {
    setCheckedBlocks((prev) => {
      const next = { ...prev }
      next[fileUsageId] = !prev[fileUsageId]
      return next
    })
  }

  useEffect(() => {
    const initial: CheckedBlocksMap = {}

    data?.data?.fileUsage.forEach((block) => {
      initial[block.fileUsageId] = true
    })

    setCheckedBlocks(initial)
  }, [data?.data?.fileUsage])

  useEffect(() => {
    onChange(
      Object.entries(checkedBlocks)
        .filter(([, value]) => value)
        .map(([key]) => key),
    )
  }, [checkedBlocks, onChange])

  return (
    <div className={s.root}>
      <div>
        <Typography styleType='heading'>{t('modal.replaceFile.title')}</Typography>
      </div>
      {loading ? (
        <Loader name='FileUsagesCheckboxList' />
      ) : (
        <>
          <FileReplacePreview file={data?.data} />
          <div className={s.selectAll}>
            <CheckboxItem
              indeterminate={allState.indeterminate}
              item={{ value: allState.checked, label: t('modal.replaceFile.selectAllUsages') }}
              name='FileUsagesCheckboxList'
              onChange={handleToggleAll}
              value={allState.checked}
            />
          </div>
          <LayoutScroll sizeAutoCapable>
            <div className={s.projectUsageWrapper}>
              {fileUsages.map((projectUsage) => (
                <FileUsageProjectItem
                  checkedBlocks={checkedBlocks}
                  key={projectUsage.projectId}
                  onToggleBlock={handleToggleBlock}
                  onToggleProject={handleToggleProject}
                  onToggleSection={handleToggleSection}
                  projectUsage={projectUsage}
                />
              ))}
            </div>
          </LayoutScroll>
        </>
      )}
    </div>
  )
}

export default FileUsagesCheckboxList
